<template>
  <BasePage :pageTitle="__DICT__.hub?.more || 'More'" show-menu show-footer>
    <div class="d-flex flex-column gap-3 mt-2">
      <!-- Inbox (notifications-v2) -->
      <div v-if="inbox.enabled" class="list-group">
        <a class="list-group-item list-group-item-action d-flex align-items-center"
           @click.prevent="navigateTo('/inbox')">
          <IconInbox class="me-3 text-muted" :size="20" />
          <span>{{ __DICT__.inbox.title }}</span>
          <span v-if="inbox.unread" class="badge bg-danger text-white ms-auto me-2">{{ unreadLabel(inbox.unread) }}</span>
          <IconChevronRight class="text-muted" :class="{ 'ms-auto': !inbox.unread }" :size="16" />
        </a>
      </div>

      <!-- Resort Section -->
      <div>
        <div class="menu-section-heading mb-2">{{ __DICT__.hub?.resort || 'Resort' }}</div>
        <div class="list-group">
          <a class="list-group-item list-group-item-action d-flex align-items-center"
             @click.prevent="navigateTo('/resort')">
            <IconMountain class="me-3 text-muted" :size="20" />
            <span>{{ __DICT__.bottomMenu.resort }}</span>
            <IconChevronRight class="ms-auto text-muted" :size="16" />
          </a>
          <a class="list-group-item list-group-item-action d-flex align-items-center"
             @click.prevent="navigateTo('/contacts')">
            <IconAddressBook class="me-3 text-muted" :size="20" />
            <span>{{ __DICT__.tray.contacts }}</span>
            <IconChevronRight class="ms-auto text-muted" :size="16" />
          </a>
        </div>
      </div>

      <!-- Resources Section -->
      <div>
        <div class="menu-section-heading mb-2">{{ __DICT__.hub?.resources || 'Resources' }}</div>
        <div class="list-group">
          <a v-if="__DICT__.handbookURLs" class="list-group-item list-group-item-action d-flex align-items-center"
             @click.prevent="navigateTo('/handbook')">
            <IconBook class="me-3 text-muted" :size="20" />
            <span>{{ __DICT__.bottomMenu.handbook }}</span>
            <IconChevronRight class="ms-auto text-muted" :size="16" />
          </a>
          <a v-else class="list-group-item list-group-item-action d-flex align-items-center"
             :href="__DICT__.handbookURL" target="_blank">
            <IconBook class="me-3 text-muted" :size="20" />
            <span>{{ __DICT__.bottomMenu.handbook }}</span>
            <IconExternalLink class="ms-auto text-muted" :size="16" />
          </a>
          <a class="list-group-item list-group-item-action d-flex align-items-center"
             @click.prevent="navigateTo('/week-info')">
            <IconClipboardList class="me-3 text-muted" :size="20" />
            <span>{{ __DICT__.menuOptions.WeeklyInfo }}</span>
            <IconChevronRight class="ms-auto text-muted" :size="16" />
          </a>
          <a class="list-group-item list-group-item-action d-flex align-items-center"
             @click.prevent="navigateTo('/week-info/season')">
            <IconFiles class="me-3 text-muted" :size="20" />
            <span>{{ __DICT__.menuOptions.SeasonInfo }}</span>
            <IconChevronRight class="ms-auto text-muted" :size="16" />
          </a>
        </div>
      </div>

      <!-- Settings Section -->
      <div>
        <div class="menu-section-heading mb-2">{{ __DICT__.hub?.settings || 'Settings' }}</div>
        <div class="list-group">
          <a v-if="notificationsV2" class="list-group-item list-group-item-action d-flex align-items-center"
             @click.prevent="navigateTo('/notifications')">
            <IconBell class="me-3 text-muted" :size="20" />
            <span>{{ __DICT__.notifications?.pushNotifications || 'Push notifications' }}</span>
            <IconChevronRight class="ms-auto text-muted" :size="16" />
          </a>
          <div class="list-group-item d-flex align-items-center justify-content-between">
            <div class="d-flex align-items-center">
              <IconMoon class="me-3 text-muted" :size="20" />
              <span>{{ __DICT__.hub?.darkMode || 'Dark Mode' }}</span>
            </div>
            <div class="form-check form-switch mb-0">
              <input
                  class="form-check-input"
                  type="checkbox"
                  :checked="theme === 'dark'"
                  @change="toggleTheme"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Account Section -->
      <div>
        <div class="menu-section-heading mb-2">{{ __DICT__.hub?.account || 'Account' }}</div>
        <div class="list-group">
          <a class="list-group-item list-group-item-action d-flex align-items-center"
             @click.prevent="changeWeek">
            <IconCalendar class="me-3 text-muted" :size="20" />
            <span>{{ __DICT__.tray.changeWeek }}</span>
            <IconChevronRight class="ms-auto text-muted" :size="16" />
          </a>
          <a class="list-group-item list-group-item-action d-flex align-items-center"
             @click.prevent="lockApp">
            <IconLock class="me-3 text-muted" :size="20" />
            <span>{{ __DICT__.tray.lock }}</span>
            <IconChevronRight class="ms-auto text-muted" :size="16" />
          </a>
          <a class="list-group-item list-group-item-action d-flex align-items-center text-danger"
             @click.prevent="logOut">
            <IconLogout class="me-3" :size="20" />
            <span>{{ __DICT__.tray.logout }}</span>
          </a>
        </div>
      </div>

      <!-- About Section -->
      <div>
        <div class="menu-section-heading mb-2">{{ __DICT__.hub?.about || 'About' }}</div>
        <div class="list-group">
          <div class="list-group-item d-flex justify-content-between">
            <span class="text-muted">{{ __DICT__.hub?.version || 'Version' }}</span>
            <span>{{ appVersion }}</span>
          </div>
          <div class="list-group-item d-flex justify-content-between" v-if="weekStore.repObj">
            <span class="text-muted">{{ __DICT__.hub?.repId || 'Rep ID' }}</span>
            <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
            <span>{{ weekStore.repObj.repId }} - {{ weekStore.repObj.level }}</span>
          </div>
        </div>
        <div class="mt-2">
          <ReportABug />
        </div>
      </div>
    </div>
  </BasePage>
</template>

<script setup>
import { ref } from 'vue'
import BasePage from "@/components/BasePage.vue"
import ReportABug from "@/components/ReportABug.vue"
import { useRouter } from "vue-router"
import { useAuthStore } from '@/stores/authStore'
import { useWeekStore } from "@/stores/week.js"
import { api } from "@/services/api.js"
import { useTheme } from "@/composables/useTheme.js"
import { useConfigFlag } from "@/composables/useConfigFlag.js"
import { version } from '../../package.json'
import { useInboxStore } from '@/stores/inbox.js'
import { unreadLabel } from '@/services/inbox.js'
import {
  IconInbox,
  IconMountain,
  IconAddressBook,
  IconBook,
  IconClipboardList,
  IconFiles,
  IconMoon,
  IconCalendar,
  IconLock,
  IconLogout,
  IconChevronRight,
  IconExternalLink,
  IconBell
} from '@tabler/icons-vue'

const router = useRouter()
const authStore = useAuthStore()
const weekStore = useWeekStore()
const { theme, toggleTheme } = useTheme()
const appVersion = ref(version)
// The subscribe page is linked for every staff member once notifications-v2 is on.
const { enabled: notificationsV2 } = useConfigFlag('notifications-v2')
const inbox = useInboxStore()

const navigateTo = (path) => {
  router.push(path)
}

const changeWeek = () => {
  localStorage.removeItem('weekId')
  router.push("/")
}

const lockApp = () => {
  authStore.setPwaUnlocked(false)
  localStorage.removeItem('lastActivity')
  router.push({ name: 'Unlock' })
}

const logOut = async () => {
  await api.logout()
  authStore.setPwaUnlocked(false)
  authStore.setLoggedIn(false)
  authStore.logOut()
  router.push({ name: 'Login' })
}
</script>

<style scoped>
.list-group-item {
  cursor: pointer;
  transition: background-color 0.2s;
}

.list-group-item:hover {
  background-color: rgba(var(--tblr-body-color-rgb), 0.03);
}
</style>
